import { useQuery } from '@tanstack/react-query'
import { X, RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock } from 'lucide-react'
import { apiClientsApi, ApiClient } from '@/lib/api'

interface ApiClientLogsModalProps {
  client: ApiClient
  onClose: () => void
}

export default function ApiClientLogsModal({ client, onClose }: ApiClientLogsModalProps) {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['api-client-logs', client.id],
    queryFn: () => apiClientsApi.getClientLogs(client.id, 100)
  })

  const logs = data?.logs || []

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString)
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'invalid_token':
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'rate_limited':
      case 'ip_not_allowed':
      case 'expired':
      case 'inactive':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />
      default:
        return <Clock className="h-4 w-4 text-slate-400" />
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Exito</span>
      case 'invalid_token':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Token invalido</span>
      case 'inactive':
        return <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">Inactivo</span>
      case 'expired':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">Expirado</span>
      case 'ip_not_allowed':
        return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">IP no permitida</span>
      case 'rate_limited':
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">Limite alcanzado</span>
      case 'error':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Error</span>
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-600">{status}</span>
    }
  }

  // Calculate some stats
  const totalAccesses = logs.length
  const successfulAccesses = logs.filter(l => l.status === 'success').length
  const failedAccesses = logs.filter(l => l.status !== 'success').length
  const avgResponseTime = logs.length > 0
    ? Math.round(logs.reduce((sum, l) => sum + (l.response_time_ms || 0), 0) / logs.length)
    : 0

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Historial de accesos
            </h2>
            <p className="text-sm text-slate-500">{client.nombre}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2 text-slate-500 hover:text-gray-700 hover:bg-slate-100 rounded-lg"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-500 hover:text-gray-700 hover:bg-slate-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="px-6 py-4 border-b bg-slate-50 grid grid-cols-4 gap-4 flex-shrink-0">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{totalAccesses}</p>
            <p className="text-xs text-slate-500">Total accesos</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{successfulAccesses}</p>
            <p className="text-xs text-slate-500">Exitosos</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{failedAccesses}</p>
            <p className="text-xs text-slate-500">Fallidos</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-primary-600">{avgResponseTime}ms</p>
            <p className="text-xs text-slate-500">Tiempo medio</p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No hay registros de acceso para este cliente
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha/Hora</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">IP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tiempo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">User Agent</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {formatDateTime(log.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">{log.ip}</code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(log.status)}
                        {getStatusBadge(log.status)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate" title={log.user_agent}>
                      {log.user_agent || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-slate-50 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}
